import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../_redux/user-actions/UserActionsAction";
import {
  copy,
  DeleteButton,
  ExportButton,
  exportToExcel,
  exportToPDF,
  FormButton,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../../_metronic/_partials/controls";
import { TeacherUsersFilter } from "./teacher-users-filter/TeacherUsersFilter";
import { TeacherUsersTable } from "./teacher-users-table/TeacherUsersTable";
import { useTeacherUsersUIContext } from "./TeacherUsersUIContext";

export function TeacherUsersCard() {
  // Teacher Users Card UI Context
  const teacherUsersUIContext = useTeacherUsersUIContext();
  const teacherUsersUIProps = useMemo(() => {
    return {
      ids: teacherUsersUIContext.ids,
      setIds: teacherUsersUIContext.setIds,
      queryParams: teacherUsersUIContext.queryParams,
      openIPListEditModal: teacherUsersUIContext.openIPListEditModal
    };
  }, [teacherUsersUIContext]);

  // Getting curret state of UserActions from store (Redux)
  const { currentState, currentActiveTerm } = useSelector(
    (state) => ({
      currentState: state.userActions,    
      currentActiveTerm: state.auth.user.currentActiveTerm
    }),
    shallowEqual
  );  
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isEditionAuthorized } = getAuthorities(authorities);

  // Teacher Users Redux state
  const dispatch = useDispatch();
    
  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Öğretmen Kullanıcı Listesini Excele Aktarır */
  const exportTeacherUsersToExcel = () => {
    dispatch(actions.getAllTeacherUsers(teacherUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
        const rows = [];
        const widths = [30, 30, 30, 30, 30, 15];
        const columns = ["TC Kimlik No", "Adı", "Soyadı", "Branşı", "Kullanıcı Grubu", "Aktif mi?"];
          
        entities.forEach(entity => rows.push([
          entity.tcKimlikNo,
          entity.name,
          entity.surname,
          entity.branch,
          entity.userGroups,
          entity.isActive ? "Aktif" : "Pasif"
        ]));
          
        const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Öğretmen Kullanıcı Listesi";
        const sheetname = "Öğretmen Kullanıcı Listesi";
        exportToExcel({ columns, rows, widths, filename, sheetname });
      });
  }
  
  /** Belli Bir Öğretmen Kullanıcı Listesini PDF e Aktarır */
  const exportTeacherUsersToPDF = () => {
    dispatch(actions.getAllTeacherUsers(teacherUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "TC Kimlik No",
          rows: entities.map(entity => entity.tcKimlikNo)
        },
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Soyadı",
          rows: entities.map(entity => entity.surname)
        },
        {
          header: "Branşı",
          rows: entities.map(entity => entity.branch)
        },
        {
          header: "Kullanıcı Grubu",
          rows: entities.map(entity => entity.userGroups)
        },
        {
          header: "Aktif mi?",
          rows: entities.map(entity => entity.isActive ? "Aktif" : "Pasif")
        }
      ];
      
      const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Öğretmen Kullanıcı Listesi";
      exportToPDF(views, filename);
    });
  }
    
  /** Belli Bir Öğretmen Kullanıcı Listesini Yazdırma Penceresini Açar */
  const printTeacherUsers = () => {
    dispatch(actions.getAllTeacherUsers(teacherUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Öğretmen Kullanıcı Listesi";
      const properties = ["TC Kimlik No", "Adı", "Soyadı", "Branşı", "Kullanıcı Grubu", "Aktif mi?"];
      
      entities.forEach(entity => {
        data.push({
          "TC Kimlik No": entity.tcKimlikNo,
          "Adı": entity.name,
          "Soyadı": entity.surname,
          "Branşı": entity.branch,
          "Kullanıcı Grubu": entity.userGroups,
          "Aktif mi?": entity.isActive ? "Aktif" : "Pasif"
        });
      });
      
      print({ data, properties, header });
    });
  }

  /** Belli Bir Öğretmen Kullanıcı Listesini Panoya Kopyalar */
  const copyTeacherUsers = () => {
    dispatch(actions.getAllTeacherUsers(teacherUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Branşı", "Kullanıcı Grubu", "Aktif mi?"];
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Öğretmen Kullanıcı Listesi";
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.branch,
        entity.userGroups,
        entity.isActive ? "Aktif" : "Pasif"
      ]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Kullanıcı Listesinin Aktiflik Durumunu Değiştirir */
  const toggleActiveness = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
    
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }
    dispatch(actions.toggleActiveness(ids)).then(result => onResult(result));
  }
    
  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
      
    if (isSuccess) {
      setTimeout(() => {
        teacherUsersUIProps.setIds([]);
        dispatch(actions.cleanUserActionsState());
        dispatch(actions.getTeacherUsers(teacherUsersUIProps.queryParams));
      }, 2500);
    }
  };

  const title = <>Öğretmen Kullanıcı Listesi{teacherUsersUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{teacherUsersUIProps.ids.length}</span>]</> :
                   ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isEditionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportTeacherUsersToExcel}
                exportToPDF={exportTeacherUsersToPDF}
                print={printTeacherUsers}
                copy={copyTeacherUsers}
              />
            }
            {
              isEditionAuthorized &&
              <>
                <FormButton
                  path="Communication/Write"
                  title="IP Listesi Düzenle"
                  method={() => teacherUsersUIProps.openIPListEditModal(teacherUsersUIProps.ids)}
                />
                <DeleteButton
                  path="General/Settings-1"
                  title="Aktif Pasif Yap"
                  method={() => toggleActiveness(teacherUsersUIProps.ids)}
                />
              </>
            }
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <TeacherUsersFilter />
        <TeacherUsersTable
          toggleActiveness={toggleActiveness}
        />
      </CardBody>
    </Card>
  );
}